import { WarningAmber } from "@mui/icons-material";
import AddOutlined from "@mui/icons-material/AddOutlined";
import { getInitialPriceFormValue } from "components/TitleFormContainer/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect, useMemo, useRef } from "react";
import type { UseFormSetError } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
    selectDraftPriceHistoryForChannel,
    selectHasDateGapError,
    selectHasNoActiveEntryError,
    selectHasNoFutureEntryError,
    selectPriceHistoryForChannel,
} from "store/priceHistory/priceHistory.selectors";
import {
    getChannelFormKey,
    priceHistorySubmit,
    setPriceHistoryErrors,
} from "store/priceHistory/priceHistory.use-cases";
import type { Currencies } from "store/settingsStore";

import type { IPriceHistoryEntry, IPriceHistoryFormValues } from "../../../store/priceHistory";
import { deleteDraftEntry } from "../../../store/priceHistory/priceHistoryStore";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import type { OverriddenChannel, TFormCreateTitle } from "../../TitleFormContainer/types";
import PriceHistoryGridRow from "./PriceHistoryGridRow";
import { shiftUtcDayISO, sortPriceHistoryEntriesAsc } from "./utils";

interface IPriceHistoryProps {
    titleId?: number;
    channel?: OverriddenChannel;
    /** Kept for call-site compatibility; the grid is always the editable variant. */
    isEdit?: boolean;
}

const nextDay = (iso: string): Date => new Date(`${shiftUtcDayISO(iso, 1).substring(0, 10)}T00:00:00`);

/**
 * Editable "unified grid" of price periods for a channel (or the default price
 * when `channel` is undefined). One row per period; leaving a row auto-saves it.
 * Two explicit ways to specialise a price: per-country prices (sub-rows) and a
 * new dated period ("Ajouter un prix sur de nouvelles dates"). All business
 * logic (VAT, validation, auto-close, draft) lives in the reused Redux thunks.
 * Read-only display of prices lives in `FinanceGrid`, not here.
 */
const ChannelPriceHistory = ({ titleId, channel }: IPriceHistoryProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const channelId = channel?.channelId;
    const isDraft = useAppSelector((state) => state.priceHistory.isDraft);
    const draftEntries = useAppSelector(selectDraftPriceHistoryForChannel(channelId));
    const entries = useAppSelector(selectPriceHistoryForChannel(channelId));
    const activeEntries = isDraft ? draftEntries : entries;

    const { getCompanySettings, countryFeatures, companyCountryCode, companyCurrency } = useCompanySettings();
    const usesMultipleCurrencies = getCompanySettings("uses_multiple_currencies");
    const usesPriceWithVat = getCompanySettings("uses_price_with_vat") && countryFeatures.showPriceTTC;

    const hasDateGapError = useAppSelector(selectHasDateGapError(channelId));
    const hasNoActiveEntryError = useAppSelector(selectHasNoActiveEntryError(channelId));
    const hasNoFutureEntryError = useAppSelector(selectHasNoFutureEntryError(channelId));

    const parentFormContext = useFormContext<TFormCreateTitle>();
    const errorKey = parentFormContext ? getChannelFormKey(parentFormContext.getValues, channelId) : undefined;

    // The wizard's "next" runs trigger() on the parent form, which only sees
    // registered fields — register a virtual one so date errors block the step.
    // Channels without any price (e.g. disabled ones) must never block.
    const hasBlockingError =
        activeEntries.length > 0 && (hasNoActiveEntryError || hasDateGapError || hasNoFutureEntryError);
    const hasBlockingErrorRef = useRef(hasBlockingError);
    hasBlockingErrorRef.current = hasBlockingError;
    useEffect(() => {
        if (!parentFormContext || !errorKey) return;
        parentFormContext.register(errorKey, { validate: () => !hasBlockingErrorRef.current });
        return () => parentFormContext.unregister(errorKey, { keepValue: true, keepDefaultValue: true });
    }, [errorKey, parentFormContext]);

    // Always chronological, oldest first: a newly added period stays at the bottom
    // whether it is still a draft or already saved — no reordering on save.
    const sortedEntries = useMemo(() => [...activeEntries].sort(sortPriceHistoryEntriesAsc), [activeEntries]);

    const columnCount = 1 + (usesMultipleCurrencies ? 1 : 0) + 2 + 1;

    const handleDelete = useCallback(
        (entry: IPriceHistoryEntry) => {
            dispatch(deleteDraftEntry(entry.id));
            if (parentFormContext) {
                dispatch(setPriceHistoryErrors({ ...parentFormContext, channelId }));
                parentFormContext.setValue("shallRecompute", true, { shouldDirty: true });
            }
        },
        [channelId, dispatch, parentFormContext],
    );

    const handleAddPeriod = useCallback(() => {
        // The new period starts after the latest known bound (end date, or start
        // date for an open-ended period) so the auto-close never produces an
        // end date before a start date
        const latestBound = activeEntries.reduce<string | null>((max, e) => {
            const bound = e.endDate ?? e.startDate;
            return bound && (!max || bound > max) ? bound : max;
        }, null);
        const today = new Date();
        const dayAfterLatest = latestBound ? nextDay(latestBound) : today;
        const source = activeEntries.find((e) => e.endDate === null) ?? activeEntries[activeEntries.length - 1];
        const price = getInitialPriceFormValue({
            companyCurrency,
            vatCountryCode: companyCountryCode,
            currency: source?.currency ?? companyCurrency,
            usesPriceWithVat,
            price: source?.price ?? 0,
        });
        dispatch(
            priceHistorySubmit({
                companyCountryCode,
                data: {
                    channelId,
                    price,
                    currency: (source?.currency ?? companyCurrency) as Currencies,
                    startDate: dayAfterLatest,
                    endDate: null,
                    childCurrencies: [],
                },
                isEditing: false,
                parentFormContext,
                setError: (() => undefined) as unknown as UseFormSetError<IPriceHistoryFormValues>,
                t,
                titleId,
                usesPriceWithVat,
                channelId,
                editEntry: null,
            }),
        );
    }, [
        activeEntries,
        channelId,
        companyCountryCode,
        companyCurrency,
        dispatch,
        parentFormContext,
        t,
        titleId,
        usesPriceWithVat,
    ]);

    const displayedFormErrors = useMemo(
        () =>
            Object.entries({
                NoActiveEntryError: hasNoActiveEntryError,
                DateGapError: hasDateGapError,
                NoFutureEntryError: hasNoFutureEntryError,
            }).map(
                ([error, value]) =>
                    value && (
                        <div className="flex items-center gap-1" key={error}>
                            <WarningAmber className="text-input-warning w-4!" />
                            <p className="text-xs text-input-warning">{t(`PriceHistory.${error}`)}</p>
                        </div>
                    ),
            ),
        [hasDateGapError, hasNoActiveEntryError, hasNoFutureEntryError, t],
    );

    return (
        // @ts-expect-error name is used by handleScrollToErrors
        <div className="flex flex-col gap-2" name={errorKey}>
            <div className="flex flex-col">{displayedFormErrors}</div>

            {sortedEntries.length > 0 && (
                <div className="overflow-x-auto rounded border border-border-color">
                    <table className="w-full table-fixed text-left">
                        <thead>
                            <tr className="border-b border-border-color bg-backgroundZ1">
                                <th className="py-2 px-2 text-xs font-semibold text-foreground whitespace-nowrap">
                                    {usesPriceWithVat ? t("NewTitle.PriceVAT") : t("NewTitle.Price")}
                                </th>
                                {usesMultipleCurrencies && (
                                    <th className="py-2 px-2 text-xs font-semibold text-foreground w-28">
                                        {t("PriceHistory.Currency")}
                                    </th>
                                )}
                                <th className="py-2 px-2 text-xs font-semibold text-foreground w-36">
                                    {t("PriceHistory.StartDate")}
                                </th>
                                <th className="py-2 px-2 text-xs font-semibold text-foreground w-36">
                                    {t("PriceHistory.EndDate")}
                                </th>
                                <th className="py-2 px-2 w-8" aria-hidden="true" />
                            </tr>
                        </thead>
                        <tbody>
                            {sortedEntries.map((entry) => (
                                <PriceHistoryGridRow
                                    key={entry.id}
                                    entry={entry}
                                    channelId={channelId}
                                    titleId={titleId}
                                    columnCount={columnCount}
                                    onDelete={() => handleDelete(entry)}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <button
                type="button"
                onClick={handleAddPeriod}
                className="flex items-center gap-1 w-fit p-0 text-xs font-medium text-info bg-transparent border-0 cursor-pointer hover:underline"
            >
                <AddOutlined sx={{ fontSize: 14 }} />
                {t("PriceHistory.AddPriceNewDates")}
            </button>
        </div>
    );
};

export default ChannelPriceHistory;
